/**
 * Skeleton loaders — placeholders animados para contenido en carga.
 *
 * - `<Skeleton>` primitivo con variant `line` | `block` | `circle`.
 * - `<TableSkeleton rows cols />` para tablas de listado.
 *
 * Usa el gris slate-200 + animate-pulse del sistema.
 */

type SkeletonVariant = 'line' | 'block' | 'circle'

type SkeletonProps = {
  variant?: SkeletonVariant
  className?: string
  width?: string | number
  height?: string | number
}

const BASE = 'bg-divider/70 animate-pulse'

const VARIANT_CLS: Record<SkeletonVariant, string> = {
  line: 'h-3 rounded',
  block: 'rounded-md',
  circle: 'rounded-full',
}

export function Skeleton({ variant = 'line', className = '', width, height }: SkeletonProps) {
  const style: React.CSSProperties = {}
  if (width !== undefined) style.width = typeof width === 'number' ? `${width}px` : width
  if (height !== undefined) style.height = typeof height === 'number' ? `${height}px` : height
  return (
    <div
      role="status"
      aria-label="Cargando…"
      className={`${BASE} ${VARIANT_CLS[variant]} ${className}`}
      style={style}
    />
  )
}

type TableSkeletonProps = {
  rows?: number
  cols?: number
  /** Índice de columnas que deben renderizar "chips" más cortos (opcional). */
  narrowCols?: number[]
  className?: string
}

export function TableSkeleton({
  rows = 6,
  cols = 5,
  narrowCols = [],
  className = '',
}: TableSkeletonProps) {
  return (
    <div role="status" aria-label="Cargando lista…" className={`divide-y divide-divider ${className}`}>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-4 px-4 py-3">
          {Array.from({ length: cols }).map((_, c) => {
            const narrow = narrowCols.includes(c)
            const isFirst = c === 0
            return (
              <Skeleton
                key={c}
                variant="line"
                className={isFirst ? 'flex-[2]' : narrow ? 'flex-[0_0_60px]' : 'flex-1'}
                height={isFirst ? 14 : 10}
              />
            )
          })}
        </div>
      ))}
    </div>
  )
}
